import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Fade,
  Avatar,
  Tooltip,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAllNotification } from '../Query/NotificationQuery';
import { useAllMarkRead } from '../Query/MarkReadQuery';
import { useNavigate, createSearchParams } from 'react-router-dom';
import {COLORS} from "../../theme/themeConstants"
interface NotificationPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const NotificationPopover: React.FC<NotificationPopoverProps> = ({ anchorEl, open, onClose }) => {
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const [page, setPage] = useState(1);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const parseNotificationData = (raw: any): any | null => {
    try {
      const dataString = typeof raw?.notificationData === 'string' ? raw.notificationData : null;
      if (!dataString) return null;
      return JSON.parse(dataString);
    } catch {
      return null;
    }
  };

  const getNotificationImageUrl = (raw: any): string | undefined => {
    const parsed = parseNotificationData(raw);
    const url = parsed?.imageUrl as string | undefined;
    return url || (raw?.notificationImage as string | undefined) || undefined;
  };

  const params = useMemo(() => {
    let userId = '';
    let userType = '';
    try {
      const storedUser = localStorage.getItem('userData');
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      userId = parsed?.userId || '';
      userType = 'merchant';
    } catch (error) {
      console.error('Failed to parse user data:', error);
    }
    return {
      userId,
      userType,
      page,
      limit: 10,
      refreshKey // Include refresh key to force cache invalidation
    };
  }, [page, refreshKey]);

  const { data: notificationData, isLoading: notificationLoading } = useAllNotification(params);
  const { mutate: markAllAsRead } = useAllMarkRead();

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [generalNotificationPopup, setGeneralNotificationPopup] = useState<{
    open: boolean;
    notification: any | null;
  }>({
    open: false,
    notification: null,
  });

  const handleRefresh = () => {
    setSnackbarMessage('Notifications refreshed');
    setShowSnackbar(true);
    setNotifications([]); // Clear existing notifications
    setPage(1); // Reset to first page
    setHasMore(true); // Reset pagination state
    setRefreshKey(prev => prev + 1); // Force cache invalidation
  };

  const navigate = useNavigate();

  const handleNotificationClick = (notification: any) => {
    // Handle GENERAL notifications with a popup
    if (notification.notificationType === "GENERAL") {
      setGeneralNotificationPopup({
        open: true,
        notification: notification,
      });
      return;
    }

    // Handle other notification types (existing logic)
    // if (notification.notificationType === "PENDING_ORDER_CONFIRMATION")
      //  {
      let orderId = notification.orderId;
      if (!orderId && notification.notificationData) {
        try {
          const data = JSON.parse(notification.notificationData);
          orderId = data.orderId;
        } catch {}
      }
      if (!orderId) {
        setSnackbarMessage('Order ID not found for this notification');
        setShowSnackbar(true);
        return;
      }
      navigate({
        pathname: '/layout/orderManagement',
        search: createSearchParams({
          orderId,
          tab: 'new',
        }).toString(),
      });
      onClose();
    // }
    // ... handle other types if needed ...
  };

  useEffect(() => {
    if (!open) return;
    if (!notificationData) return;
    const newData = notificationData.notifications || [];
    setNotifications(page === 1 ? newData : [...notifications, ...newData]);
    setHasMore(notificationData.page * notificationData.limit < notificationData.totalRecords);
  }, [page, open, notificationData]);

  useEffect(() => {
    if (!open) {
      setNotifications([]);
      setPage(1);
      setHasMore(true);
    }
  }, [open]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (notificationLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore && !notificationLoading) {
          setPage(prev => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [notificationLoading, hasMore]
  );

  useEffect(() => {
    if (open) {
      let userId = '';
      let userType = 'merchant';
      try {
        const storedUser = localStorage.getItem('userData');
        const parsed = storedUser ? JSON.parse(storedUser) : null;
        userId = parsed?.userId || '';
        // const rawUserType = parsed?.userType || '';
        // userType = (rawUserType === 'superAdmin' || rawUserType === 'subadmin') ? 'admin' : rawUserType;
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
      markAllAsRead({ userId, userType, isRead: true });
    }
  }, [open, markAllAsRead]);

  // Prevent body scrolling when modal is open
  // useEffect(() => {
  //   if (open) {
  //     document.body.style.overflow = 'hidden';
  //   } else {
  //     document.body.style.overflow = 'unset';
  //   }

  //   return () => {
  //     document.body.style.overflow = 'unset';
  //   };
  // }, [open]);

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock={false}
        PaperProps={{
          sx: {
            width: 650,
            borderRadius: 2,
            p: 0,
            overflowX: 'hidden',
            maxHeight: '80vh'
          }
        }}
      >
        <Box
          sx={{
            maxHeight: 'calc(80vh - 80px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            background: '#ffffff',
            padding: 0
          }}
        >
          <Box
            sx={{
              background: COLORS.primary.main,
              color: COLORS.secondary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid #ececec',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              <NotificationsIcon sx={{ fontSize: 24 }} />
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: 18,
                textAlign: isRTL ? 'right' : 'left'
              }}>
                Notifications
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  sx={{ color: COLORS.secondary.main }}
                  disabled={notificationLoading}
                  size="small"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={onClose}
                sx={{ color: COLORS.secondary.main }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {notifications.length === 0 && !notificationLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                textAlign: 'center'
              }}
            >
              <NotificationsIcon sx={{ fontSize: 64, color: '#ccc', marginBottom: 2 }} />
              <Typography variant="h6" color="textSecondary" sx={{ textAlign: isRTL ? 'center' : 'center' }}>
                No notifications
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: isRTL ? 'center' : 'center' }}>
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            <List sx={{ padding: 0 }}>
              {notifications.map((notification, index) => {
                const listItemContent = (
                  <ListItem
                    component="div"
                    sx={{
                      padding: '16px 20px',
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(103, 126, 234, 0.1)',
                        transform: 'translateX(4px)',
                      }
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: COLORS.primary.main,
                          color: COLORS.secondary.main
                        }}
                        src={getNotificationImageUrl(notification)}
                      >
                        {/* <NotificationsIcon /> */}
                        <NotificationsActiveIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 700, 
                            color: 'text.primary', 
                            wordBreak: 'break-word', 
                            overflowWrap: 'anywhere',
                            textAlign: isRTL ? 'right' : 'left'
                          }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                          {/* Display order number if available */}
                          {(() => {
                            try {
                              const notificationData = notification.notificationData ? JSON.parse(notification.notificationData) : null;
                              const orderNumber = notificationData?.orderNumber;
                              return orderNumber ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: COLORS.secondary.main,
                                    fontWeight: 600,
                                    marginBottom: 1,
                                    fontSize: '0.875rem',
                                    textAlign: isRTL ? 'right' : 'left'
                                  }}
                                >
                                  Order: {orderNumber}
                                </Typography>
                              ) : null;
                            } catch (error) {
                              return null;
                            }
                          })()}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.4,
                              marginBottom: 1,
                              fontWeight: 500,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              textAlign: isRTL ? 'right' : 'left'
                            }}
                          >
                            {notification.body}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            flexDirection: isRTL ? 'row-reverse' : 'row'
                          }}>
                            <Chip
                              label={notification.notificationType}
                              size="small"
                              sx={{
                                backgroundColor: COLORS.primary.main,
                                color: COLORS.secondary.main,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ 
                                marginLeft: isRTL ? '0' : 'auto',
                                marginRight: isRTL ? 'auto' : '0'
                              }}
                            >
                              {notification.createdAt}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                );

                return (
                  <Fade in={true} timeout={300 + index * 100} key={notification.id}>
                    {index === notifications.length - 1 ? (
                      <div ref={lastNotificationRef}>{listItemContent}</div>
                    ) : (
                      listItemContent
                    )}
                  </Fade>
                );
              })}
              {notificationLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </List>
          )}
        </Box>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMessage}
        />
      </Popover>

      {/* GENERAL Notification Popup */}
      <Dialog
        open={generalNotificationPopup.open}
        onClose={() => setGeneralNotificationPopup({ open: false, notification: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '84vh',
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: COLORS.primary.main,
            color: COLORS.secondary.main,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '18px 24px',
            borderRadius: '12px 12px 0 0',
            flexDirection: isRTL ? 'row-reverse' : 'row'
          }}
        >
          <NotificationsIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            fontSize: 18, 
            letterSpacing: 0.2,
            textAlign: isRTL ? 'right' : 'left'
          }}>
            Notification Details
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ padding: 4, backgroundColor: '#faf9fb' }}>
          {generalNotificationPopup.notification && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Title */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  marginBottom: 1,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere'
                }}
              >
                {generalNotificationPopup.notification.title}
              </Typography>

              {/* Image */}
              {getNotificationImageUrl(generalNotificationPopup.notification) && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: 'white',
                      border: '1px solid rgba(0,0,0,0.06)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                    }}
                  >
                    <img
                      src={getNotificationImageUrl(generalNotificationPopup.notification)}
                      alt="Notification"
                      style={{
                        maxWidth: '200px',
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: '12px'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Body */}
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                  fontSize: 16,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.06)',
                  padding: 2
                }}
              >
                {generalNotificationPopup.notification.body}
              </Typography>

              {/* Notification Type and Date */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  marginTop: 1,
                  paddingTop: 2,
                  borderTop: '1px dashed rgba(0,0,0,0.15)'
                }}
              >
                {/* <Chip
                  label={generalNotificationPopup.notification.notificationType}
                  size="small"
                  sx={{
                    backgroundColor: '#a84d6e',
                    color: 'white',
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    borderRadius: 2,
                    fontSize: '0.72rem'
                  }}
                /> */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ marginLeft: 'auto' }}
                >
                  {generalNotificationPopup.notification.createdAt}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ padding: '16px 24px', backgroundColor: '#faf9fb' }}>
          <Button
            onClick={() => setGeneralNotificationPopup({ open: false, notification: null })}
            variant="contained"
            sx={{
              backgroundColor: COLORS.primary.main,
              '&:hover': {
                backgroundColor: '#4a0024',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationPopover;
